// styles/CategoriaStyles.js - Todos los estilos del componente de categorías
import { styled, createTheme } from "@mui/material/styles";
import { Box, Button, Fab, alpha } from "@mui/material";

// Theme
export const theme = createTheme({
  palette: {
    primary: { main: "#432918", light: "#6B4226", dark: "#2A1A0F", contrastText: "#FFF8F0" },
    background: { paper: "#FFF8F0", card: "#F8EDE3", section: "#FFFFF" },
    text: { primary: "#1A1A1A", secondary: "#A67A44" },
    border: { light: "#D4C4B0", main: "#D4C4B0" },
    accent: { main: "#8B5A2B", light: "#B8A091", dark: "#6B4423" },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Layout Principal
export const ResponsiveMainLayout = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: theme.palette.background.section,
  padding: "16px",
  boxSizing: "border-box",
}));

// Navegación Superior
export const TopNavigation = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: "#FFF",
  borderBottom: `1px solid ${theme.palette.border.light}`,
  padding: "16px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  '@media (max-width: 768px)': {
    padding: "12px",
  },
}));

export const NavHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: "16px",
  '@media (max-width: 768px)': {
    alignItems: "flex-start",
    gap: "12px",
  },
}));

export const AsociacionesContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "30px",
  overflowX: "auto",
  justifyContent: "center",
  padding: "1px 0",
  '&::-webkit-scrollbar': {
    height: "8px",
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: "#f1f1f1",
    borderRadius: "4px",
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.border.light,
    borderRadius: "4px",
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  '@media (max-width: 768px)': {
    gap: "8px",
  },
}));

// Chips de Asociaciones
export const AsociacionChip = styled(Box)(({ theme, selected }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  padding: "30px 30px",
  borderRadius: "8px",
  cursor: "pointer",
  border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.border.light}`,
  backgroundColor: selected ? theme.palette.primary.main : "#FFFFFF",
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  transition: "all 0.3s ease",
  minWidth: "120px",
  textAlign: "center",
  position: "relative",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(67, 41, 24, 0.15)",
  },
  '@media (max-width: 768px)': {
    padding: "10px 12px",
    minWidth: "100px",
  },
}));

export const GubernamentalChip = styled(AsociacionChip)(({ theme, expanded }) => ({
  border: `2px solid ${theme.palette.accent.main}`,
  backgroundColor: expanded ? theme.palette.accent.main : "#FFFFFF",
  color: expanded ? theme.palette.primary.contrastText : theme.palette.accent.main,
  "&:hover": {
    backgroundColor: expanded ? theme.palette.accent.dark : "rgba(139, 90, 43, 0.1)",
  },
}));

export const ChipHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "4px",
});

export const ChipTitle = styled(Box)(({ theme, selected }) => ({
  fontWeight: 700,
  fontSize: "0.9rem",
  color: "inherit",
  lineHeight: 1.2,
  '@media (max-width: 768px)': {
    fontSize: "0.85rem",
  },
  '@media (max-width: 480px)': {
    fontSize: "0.8rem",
  }
}));

export const ChipSubtitle = styled(Box)(({ theme, selected }) => ({
  fontSize: "0.75rem",
  opacity: selected ? 0.9 : 0.7,
  color: "inherit",
  fontWeight: 500,
  '@media (max-width: 768px)': {
    fontSize: "0.7rem",
  },
  '@media (max-width: 480px)': {
    fontSize: "0.65rem",
  }
}));

export const BadgeStyled = styled(Box)(({ theme, selected, isGubernamental }) => ({
  position: "absolute",
  top: "-8px",
  right: "-8px",
  backgroundColor: selected 
    ? "#FFF" 
    : isGubernamental 
      ? theme.palette.accent.main 
      : theme.palette.primary.main,
  color: selected 
    ? isGubernamental 
      ? theme.palette.accent.main 
      : theme.palette.primary.main
    : "#FFF",
  borderRadius: "12px",
  padding: "2px 8px",
  fontSize: "0.7rem",
  fontWeight: "bold",
  minWidth: "20px",
  textAlign: "center",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  border: `2px solid ${selected ? (isGubernamental ? theme.palette.accent.main : theme.palette.primary.main) : "#FFF"}`,
  '@media (max-width: 768px)': {
    fontSize: "0.65rem",
    padding: "1px 6px",
    top: "-6px",
    right: "-6px",
  }
}));

// Contenido Principal
export const MainContentResponsive = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "16px",
  backgroundColor: theme.palette.background.section,
  '@media (max-width: 768px)': {
    padding: "12px",
  },
}));

export const ContentAreaResponsive = styled(Box)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  padding: "16px",
  boxShadow: "0 4px 12px rgba(67, 41, 24, 0.1)",
  border: `1px solid ${theme.palette.border.light}`,
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  boxSizing: "border-box",
  '@media (max-width: 768px)': {
    padding: "12px",
    borderRadius: "6px",
  },
}));

// Secciones de Categorías
export const CategoriaSection = styled(Box)(({ theme }) => ({
  marginBottom: "16px",
  borderRadius: "8px",
  border: `1px solid ${theme.palette.border.light}`,
  backgroundColor: "#FFFFFF",
  transition: "all 0.3s ease",
  '&:hover': {
    boxShadow: "0 4px 12px rgba(67, 41, 24, 0.08)",
  },
  '@media (max-width: 768px)': {
    marginBottom: "12px",
  },
}));

export const CategoriaHeader = styled(Box)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  padding: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",
  borderBottom: `1px solid ${theme.palette.border.light}`,
  '&:hover': {
    backgroundColor: theme.palette.background.card,
  },
  '@media (max-width: 768px)': {
    padding: "12px",
  },
}));

export const RepresentanteRow = styled(Box)(({ theme }) => ({
  padding: "16px",
  borderBottom: `1px solid ${theme.palette.border.light}`,
  backgroundColor: "#FFF",
  "&:hover": {
    backgroundColor: theme.palette.background.card,
  },
  "&:last-child": {
    borderBottom: "none",
  },
  '@media (max-width: 768px)': {
    padding: "12px",
  },
}));

export const CategoryIconBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  padding: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `2px solid ${theme.palette.accent.light}`,
  width: "40px",
  height: "40px",
  '@media (max-width: 768px)': {
    width: "36px",
    height: "36px",
    padding: "6px",
  },
}));

// Botones y FAB
export const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: "8px",
  '&.MuiButton-contained': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  '&.MuiButton-outlined': {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
  },
}));

export const SearchFab = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: 24,
  right: 24,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  '@media (max-width: 768px)': {
    bottom: 16,
    right: 16,
    width: 48,
    height: 48,
  }
}));